import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  CampaignService,
  PublicCommonWebsiteLandingViewedPayload,
} from '../../services/campaign.service';
import {
  CommonWebsiteSuccessPageApi,
  mapLandingApiToModal,
  mapSuccessApiToModal,
  PublicCampaignLocationApi,
  PublicCampaignShopApi,
  SMS_CAMPAIGN_PUBLIC_QUERY_KEYS,
  validateLandingPageInput,
} from '../campaign-editor/optional-setting/common-website-content.mapper';
import { SmsCampaignConsentTermsModalComponent } from './sms-campaign-consent-terms-modal.component';
import {
  DEFAULT_LANDING_PAGE_DATA,
  LandingPageModalData,
} from '../campaign-editor/optional-setting/create-modal-landing-page/create-modal-landing-page.component';
import { SuccessPageModalData } from '../campaign-editor/optional-setting/create-modal-success-page/create-modal-success-page.component';
import {
  buildConsentTextSegments,
  ConsentTextSegment,
} from '../campaign-editor/optional-setting/consent-link.util';
import { persistSmsSuccessContact } from '../campaign-editor/optional-setting/success-page-merge-fields.util';
import {
  persistSmsCampaignTrackingToken,
  readSmsCampaignTrackingToken,
  SMS_CAMPAIGN_TRACKING_TOKEN_QUERY_KEY,
  withoutTrackingTokenQueryParam,
} from '../campaign-editor/optional-setting/sms-campaign-tracking.util';

interface SmsSuccessNavigationState {
  contactValue?: string;
  successPage?: SuccessPageModalData;
  landingLogo?: string;
}

@Component({
  selector: 'app-sms-campaign-landing-page',
  standalone: true,
  imports: [CommonModule, FormsModule, SmsCampaignConsentTermsModalComponent],
  templateUrl: './sms-campaign-landing-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sms-campaign-landing-page.component.scss',
})
export class SmsCampaignLandingPageComponent implements OnInit, OnDestroy {
  @ViewChild('smsLandingInputEl') smsLandingInputEl?: ElementRef<HTMLInputElement>;

  landingPage: LandingPageModalData = { ...DEFAULT_LANDING_PAGE_DATA };
  inputValue = '';
  inputError = '';
  submitError = '';
  submitting = false;
  loading = false;
  error: string | null = null;
  campaignId: string | null = null;
  showConsentTermsModal = false;
  shopInfo: PublicCampaignShopApi | null = null;
  locationInfo: PublicCampaignLocationApi | null = null;

  private routeSub?: Subscription;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private campaignService: CampaignService
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.queryParamMap.subscribe(params => {
      const nextCampaignId = params.get('campaignId')?.trim() || null;
      const campaignIdChanged = nextCampaignId !== this.campaignId;
      this.campaignId = nextCampaignId;
      this.trackLandingViewedIfNeeded(params);
      if (campaignIdChanged) {
        this.loadCampaignContent();
      }
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  getLandingInputPlaceholder(): string {
    const inputType = this.landingPage.inputType?.toLowerCase();
    if (inputType === 'email') {
      return 'e.g. user@example.com';
    }
    return 'e.g. 555-555-5555';
  }

  getInputTypeAttr(): string {
    return this.landingPage.inputType?.toLowerCase() === 'email' ? 'email' : 'tel';
  }

  onInputChange(): void {
    if (this.getInputTypeAttr() === 'tel') {
      this.applyPhoneMask();
    }
    if (this.inputError) {
      this.inputError = '';
    }
    if (this.submitError) {
      this.submitError = '';
    }
  }

  private applyPhoneMask(): void {
    const inputEl = this.smsLandingInputEl?.nativeElement;
    const cursorPos = inputEl?.selectionStart ?? this.inputValue.length;
    const digitsBeforeCursor = this.inputValue.slice(0, cursorPos).replace(/\D/g, '').length;

    const formatted = this.formatPhoneNumber(this.inputValue);
    if (formatted === this.inputValue) {
      return;
    }
    this.inputValue = formatted;

    if (!inputEl) {
      return;
    }
    setTimeout(() => {
      const newCursorPos = this.cursorPositionAfterDigits(formatted, digitsBeforeCursor);
      inputEl.setSelectionRange(newCursorPos, newCursorPos);
    });
  }

  private formatPhoneNumber(value: string): string {
    let digits = value.replace(/\D/g, '');
    const hasLeadingOne = digits.startsWith('1');
    digits = hasLeadingOne ? digits.slice(0, 11) : digits.slice(0, 10);
    const rest = hasLeadingOne ? digits.slice(1) : digits;

    const parts: string[] = [];
    if (hasLeadingOne) {
      parts.push('1');
    }
    if (rest.length > 0) {
      parts.push(rest.slice(0, 3));
    }
    if (rest.length > 3) {
      parts.push(rest.slice(3, 6));
    }
    if (rest.length > 6) {
      parts.push(rest.slice(6, 10));
    }
    return parts.join('-');
  }

  private cursorPositionAfterDigits(formatted: string, digitCount: number): number {
    if (digitCount <= 0) {
      return 0;
    }
    let seen = 0;
    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        seen++;
        if (seen === digitCount) {
          return i + 1;
        }
      }
    }
    return formatted.length;
  }

  get hasConsentUrl(): boolean {
    return Boolean(this.landingPage.consentUrl?.trim());
  }

  getConsentSegments(): ConsentTextSegment[] {
    return buildConsentTextSegments(
      this.landingPage.consentText,
      this.landingPage.consentLabel,
      this.landingPage.consentUrl
    );
  }

  openConsentTermsModal(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.showConsentTermsModal = true;
  }

  closeConsentTermsModal(): void {
    this.showConsentTermsModal = false;
  }

  onSubmit(): void {
    if (this.submitting) {
      return;
    }

    const contact = this.inputValue.trim();
    const validationError = validateLandingPageInput(contact, this.landingPage);
    if (validationError) {
      this.inputError = validationError;
      this.submitError = '';
      return;
    }

    if (!this.campaignId) {
      this.submitError = 'Campaign ID is missing from the link.';
      return;
    }

    this.inputError = '';
    this.submitError = '';
    this.submitting = true;

    const isPhone = this.getInputTypeAttr() === 'tel';
    const recipient = isPhone ? contact.replace(/\D/g, '') : contact;

    const offerPayload: {
      campaign_id: string;
      recipient: string;
      tracking_token?: string;
    } = {
      campaign_id: this.campaignId,
      recipient,
    };

    const trackingToken = this.resolveTrackingToken();
    if (trackingToken) {
      offerPayload.tracking_token = trackingToken;
    }

    this.campaignService.sendPublicCommonWebsiteOffer(offerPayload).subscribe({
      next: response => {
        this.submitting = false;
        if (!response.status) {
          this.submitError = response.message || 'Unable to submit. Please try again.';
          return;
        }

        this.navigateToSuccessPage(contact);
      },
      error: err => {
        this.submitting = false;
        this.submitError = err?.error?.message || 'Unable to submit. Please try again.';
      },
    });
  }

  private navigateToSuccessPage(contact: string): void {
    const successPage = this.getSuccessPageFromCache();
    if (this.campaignId) {
      persistSmsSuccessContact(this.campaignId, contact);
    }
    const state: SmsSuccessNavigationState = {
      contactValue: contact,
      landingLogo: this.landingPage.logo || '',
    };
    if (successPage) {
      state.successPage = successPage;
    }

    this.router.navigate(['/sms-campaign-success-page'], {
      queryParams: this.buildPreservedQueryParams(this.route.snapshot.queryParamMap),
      state,
    });
  }

  private resolveTrackingToken(): string {
    const fromUrl = this.route.snapshot.queryParamMap
      .get(SMS_CAMPAIGN_TRACKING_TOKEN_QUERY_KEY)
      ?.trim();
    if (fromUrl) {
      return fromUrl;
    }

    if (this.campaignId) {
      return readSmsCampaignTrackingToken(this.campaignId);
    }

    return '';
  }

  private trackLandingViewedIfNeeded(params: ParamMap): void {
    const campaignId = params.get('campaignId')?.trim();
    if (!campaignId) {
      return;
    }

    if (readSmsCampaignTrackingToken(campaignId)) {
      this.stripTrackingTokenFromUrl();
      return;
    }

    const urlTrackingToken = params.get(SMS_CAMPAIGN_TRACKING_TOKEN_QUERY_KEY)?.trim();
    const payload: PublicCommonWebsiteLandingViewedPayload = {};

    if (urlTrackingToken) {
      payload.tracking_token = urlTrackingToken;
    }

    for (const key of [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'utm_term',
    ] as const) {
      const value = params.get(key)?.trim();
      if (value) {
        payload[key] = value;
      }
    }

    this.campaignService.trackPublicCommonWebsiteLandingViewed(campaignId, payload).subscribe({
      next: response => {
        if (!response.status) {
          return;
        }

        const tokenToStore = urlTrackingToken || response.data?.tracking_token?.trim() || '';
        if (tokenToStore) {
          persistSmsCampaignTrackingToken(campaignId, tokenToStore);
          this.stripTrackingTokenFromUrl();
        }
      },
    });
  }

  private stripTrackingTokenFromUrl(): void {
    if (!this.route.snapshot.queryParamMap.has(SMS_CAMPAIGN_TRACKING_TOKEN_QUERY_KEY)) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [SMS_CAMPAIGN_TRACKING_TOKEN_QUERY_KEY]: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private loadCampaignContent(): void {
    if (!this.campaignId) {
      this.error = 'Campaign ID is missing from the link.';
      this.loading = false;
      return;
    }

    this.loading = true;
    this.error = null;
    this.inputError = '';

    this.campaignService.getPublicCommonWebsiteContent(this.campaignId).subscribe({
      next: response => {
        this.loading = false;
        if (!response.status || !response.data?.landing_page) {
          this.error = response.message || 'Unable to load campaign page.';
          return;
        }

        this.landingPage = mapLandingApiToModal(response.data.landing_page);
        this.shopInfo = response.data.shop ?? null;
        this.locationInfo = response.data.location ?? null;
        this.cacheSuccessPage(response.data.success_page);
      },
      error: err => {
        this.loading = false;
        this.error = err?.error?.message || 'Unable to load campaign page.';
      },
    });
  }

  private successPageCache: SuccessPageModalData | null = null;

  private cacheSuccessPage(successPage: CommonWebsiteSuccessPageApi | undefined): void {
    this.successPageCache = successPage ? mapSuccessApiToModal(successPage) : null;
  }

  private getSuccessPageFromCache(): SuccessPageModalData | undefined {
    return this.successPageCache ?? undefined;
  }

  private buildPreservedQueryParams(paramMap: ParamMap): Record<string, string> {
    const queryParams: Record<string, string> = {};
    for (const key of SMS_CAMPAIGN_PUBLIC_QUERY_KEYS) {
      const value = paramMap.get(key);
      if (value) {
        queryParams[key] = value;
      }
    }
    return withoutTrackingTokenQueryParam(queryParams);
  }
}
